import { FunctionComponent, useCallback, useContext, useRef, useState, useEffect, ChangeEvent } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Select, SelectChangeEvent, styled, Switch, Tooltip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCogs, faObjectGroup, faSquareList, faTableList } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { ISourceDataFormSettings } from '@/utilities/forms/SourceDataFormProcessor';

const SettingsDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(0),
    },

    '& .MuiDialogActions-root': {
      padding: theme.spacing(2),
    },
}));

const Field = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
}));

interface ISourceDataFormProps {
    settings: ISourceDataFormSettings;
    setSettings: (update :((current: ISourceDataFormSettings) => ISourceDataFormSettings)) => void;
}

const SourceDataFormSettings: FunctionComponent<ISourceDataFormProps> = ({
    settings: formSettings,
    setSettings: setFormSettings
}) => {
    const [settings, setSettings] = useState(formSettings);

    useEffect(() => {
        setSettings(formSettings);
    }, [formSettings, setSettings])

    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const onChange = useCallback((e: SelectChangeEvent<'Expanding' | 'Titled' | undefined>) => {
        setSettings(c => ({...c, [e.target.name]: e.target.value }))
    }, [setSettings]);

    const onUpdate = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setSettings(c => ({...c, [e.target.name]: checked }))
    }, [setSettings]);

    const onSave = useCallback(() => {
        handleClose();
        setFormSettings(current => settings);
    }, [handleClose, setFormSettings, settings])

    return (
        <Box>
            <Tooltip title="Settings">
                <IconButton onClick={handleClickOpen}>
                    <FontAwesomeIcon icon={faCogs} />
                </IconButton>
            </Tooltip>
            <SettingsDialog
                disablePortal
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, alignItems: 'center' }}>
                    Settings
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* <Field>
                        <Typography>
                            Layout Mode
                        </Typography>
                        <Box>
                            <Select name="layoutType" value={settings.layoutType} onChange={onChange}>
                                <MenuItem value='Titled'>Titled</MenuItem>
                                <MenuItem value='Expanding'>Expanding</MenuItem>
                            </Select>
                        </Box>
                    </Field>
                    <Divider /> */}
                    <Field>
                        <Typography>
                            Hide events that have not been created in OpenClinica
                        </Typography>
                        <Box>
                            <Switch name="hideEvents" checked={settings.hideEvents ?? false} onChange={onUpdate} />
                        </Box>
                    </Field>
                    <Divider />
                    <Field>
                        <Typography>
                            Hide forms that have not been created in OpenClinica
                        </Typography>
                        <Box>
                            <Switch name="hideForms" checked={settings.hideForms ?? false} onChange={onUpdate} />
                        </Box>
                    </Field>
                    <Divider />
                    <Field>
                        <Typography>
                            Hide versions that have not been configured for source data verification
                        </Typography>
                        <Box>
                            <Switch name="hideVersions" checked={settings.hideVersions ?? false} onChange={onUpdate} />
                        </Box>
                    </Field>
                    {
                        settings.layoutType === 'Expanding' && (
                            <>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Automatically expand events ( <FontAwesomeIcon icon={faCalendarDays} /> )
                                    </Typography>
                                    <Box>
                                        <Switch name="autoEvents" checked={settings.autoEvents ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Automatically expand forms ( <FontAwesomeIcon icon={faSquareList} /> )
                                    </Typography>
                                    <Box>
                                        <Switch name="autoForms" checked={settings.autoForms ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Automatically expand groups ( <FontAwesomeIcon icon={faObjectGroup} /> )
                                    </Typography>
                                    <Box>
                                        <Switch name="autoGroups" checked={settings.autoGroups ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Automatically expand collections ( <FontAwesomeIcon icon={faTableList} /> )
                                    </Typography>
                                    <Box>
                                        <Switch name="autoCollections" checked={settings.autoCollections} onChange={onUpdate} />
                                    </Box>
                                </Field>
                            </>
                        )
                    }
                    <Divider />
                    <Field>
                        <Typography>
                            Show discrepancy notes
                        </Typography>
                        <Box>
                            <Switch name="showNotes" checked={settings.showNotes ?? false} onChange={onUpdate} />
                        </Box>
                    </Field>
                    <Divider />
                    <Field>
                        <Typography>
                            Show field audit history
                        </Typography>
                        <Box>
                            <Switch name="showAudit" checked={settings.showAudit ?? false} onChange={onUpdate} />
                        </Box>
                    </Field>
                    {
                        settings.layoutType === 'Titled' && (
                            <>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Show side navigation
                                    </Typography>
                                    <Box>
                                        <Switch name="showSideNavigation" checked={settings.showSideNavigation ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Show forms in side navigation
                                    </Typography>
                                    <Box>
                                        <Switch name="showSideNavigationForms" checked={settings.showSideNavigationForms ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Show reviewed progress in side navigation
                                    </Typography>
                                    <Box>
                                        <Switch name="showSideNavigationReviewProgress" checked={settings.showSideNavigationReviewProgress ?? false} onChange={onUpdate} />
                                    </Box>
                                </Field>
                                <Divider />
                                <Field>
                                    <Typography>
                                        Show verification progress in side navigation
                                    </Typography>
                                    <Box>
                                        <Switch name="showSideNavigationVerificationProgress" checked={settings.showSideNavigationVerificationProgress} onChange={onUpdate} />
                                    </Box>
                                </Field>
                            </>
                        )
                    }
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={onSave}>
                        Save
                    </Button>
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                </DialogActions>
            </SettingsDialog>
        </Box>
    );
}

export default SourceDataFormSettings;